import { handleActions } from 'redux-actions';
import { Actions, getEventLogSuccess, getBuildingSuccess, getBuildingThemesFailure, getBuildingThemesRequest, getBuildingThemesSuccess, buildingFetched, getBuildingsSuccess, getBuildingsFailure, createBuildingSuccess, updateBuildingSuccess } from 'actions/buildingActions';
import { Actions as DeviceActions, createDevicesSuccess, updateDeviceSuccess } from 'actions/deviceActions';
import { Actions as UserActions, createUserSuccess } from 'actions/userActions';
import { Actions as EditorActions } from 'actions/editorActions';
import { Actions as BuildingUserActions, assignBuildingUserSuccess } from 'actions/buildingUserActions';
import { Actions as ThemeActions } from 'actions/themeActions';
import { Actions as SessionActions } from 'actions/sessionActions';
import { Building, Device, DeviceEvent, DeviceStatus, User, DeviceFile, AugmentedBuilding, ThemeId } from 'shared/models';
import { merge, Dictionary, omit, uniq, filter } from 'lodash';

export type State = Readonly<{
    data: Dictionary<AugmentedBuilding>;
    loading: Dictionary<boolean>;
    loaded: boolean;
    error: string | null;
    lastSearch: {
        order: string[];
        more: boolean;
    };
    loadingThemes: Record<ThemeId, boolean>;
    themesError: Dictionary<Error | null>;
}>;

export const initialState = Object.freeze<State>({
    data: {},
    loading: {},
    loaded: false,
    error: null,
    lastSearch: {
        order: [],
        more: false,
    },
    loadingThemes: {},
    themesError: {},
});


export const defaultDeviceScript = JSON.stringify({
    commands: [ "update theme" ],
    checkin: 900,
}, null, 2);




// Helper method to add an array of devices into the given state
const addDevices = (state: State, buildingId: string, devicesToAdd: Device[]) => {
    const { data } = state;
    const building = data[buildingId] || {};
    const { devices = {} } = building;
    return {
        ...state,
        data: {
            ...data,
            [buildingId]: {
                ...building,
                devices: {
                    ...devices,
                    ...merge({}, ...devicesToAdd.map(d => ({ [d.id]: d }))),
                },
            },
        },
    };
};

// Helper method to add an array of events into the given state
const addEvents = (state: State, buildingId: string, events: DeviceEvent[]) =>
    merge({}, state, ...events.map(e => ({ data: { [buildingId]: { events: { [e.id]: e } } } })))
;

const updateBuilding = <T extends keyof AugmentedBuilding>(t: T) => (state: State, buildingId: string | undefined, fn: (t: Readonly<AugmentedBuilding[T]>) => Readonly<AugmentedBuilding[T]>): State => {
    if (!buildingId) return state;
    const building = state.data[buildingId];
    if (!building) return state;
    return {
        ...state,
        data: {
            ...state.data,
            [buildingId]: {
                ...building,
                [t]: fn(building[t] as AugmentedBuilding[T]),
            },
        },
    };
};

const updateBuildingCredentials = updateBuilding('credentials');
const updateBuildingUsers = updateBuilding('userIds');
const updateBuildingThemes = updateBuilding('themeIds');

export const buildingsReducer = handleActions({
    [Actions.CREATE_BUILDING_SUCCESS]: (state: State, action: ReturnType<typeof createBuildingSuccess>) => {
        const { building } = action.payload;
        return merge({}, state, { data: { [building.id]: building } });
    },


    [Actions.GET_BUILDINGS_REQUEST]: (state: State /*, action: ReturnType<typeof getBuildingsRequest>*/) => {
        return merge({}, state, { loading: { all: true } });
    },
    [Actions.GET_BUILDINGS_SUCCESS]: (state: State, action: ReturnType<typeof getBuildingsSuccess>) => {
        const { buildings, more } = action.payload;
        const { page } = action.meta;

        const thisBatchOrder = buildings.map(b => b.id);
        const order =
            page === 1
                ? thisBatchOrder
                : [
                    ...state.lastSearch.order,
                    ...thisBatchOrder,
                ]
        ;
        return {
            ...state,
            data: merge({}, state.data,
                ...buildings.map((b: Building) => ({
                    [b.id]: {
                        ...(state.data[b.id] || {}),
                        ...b,
                    },
                }))
            ),
            loaded: true,
            loading: omit(state.loading, 'all'),
            error: null,
            lastSearch: {
                order: uniq(order),
                more,
            },
        };
    },
    [Actions.GET_BUILDINGS_FAILURE]: (state: State, action: ReturnType<typeof getBuildingsFailure>) => {
        return {
            ...state,
            error: action.payload.message,
            loading: omit(state.loading, 'all'),
        };
    },

    [Actions.GET_BUILDING_THEMES_REQUEST]: (state: State, action: ReturnType<typeof getBuildingThemesRequest>) => {
        const buildingId = action.payload;
        return merge({}, state, {
            loadingThemes: {
                [buildingId]: true,
            },
            themesError: {
                [buildingId]: null,
            },
        });
    },
    [Actions.GET_BUILDING_THEMES_SUCCESS]: (state: State, action: ReturnType<typeof getBuildingThemesSuccess>) => {
        const themes = action.payload;
        const { buildingId } = action.meta;
        const themeIds = themes.map((t: any) => t.id);
        return {
            ...state,
            data: merge({}, state.data, { [buildingId]: { themeIds } }),
            loadingThemes: omit(state.loadingThemes, buildingId),
        };
    },
    [Actions.GET_BUILDING_THEMES_FAILURE]: (state: State, action: ReturnType<typeof getBuildingThemesFailure>) => {
        const { buildingId } = action.meta;
        return {
            ...state,
            loadingThemes: omit(state.loadingThemes, buildingId),
            themesError: { ...state.themesError, [buildingId]: action.payload },
        };
    },

    [Actions.GET_BUILDING_REQUEST]: (state: State, action: any) => {
        const urlSlugOrId = action.payload;
        return merge({}, state, { loading: { [urlSlugOrId]: true } });
    },
    [Actions.GET_BUILDING_SUCCESS]: (state: State, action: ReturnType<typeof getBuildingSuccess>) => {
        const { building, events = [], hasMoreEvents, credentials, broadcastMessage /*, themes, content */, users = [] } = action.payload;
        const { urlSlugOrId } = action.meta;

        const currentBuilding = state.data[building.id] || {};

        const _building = {
            ...building,
            devices: merge({}, currentBuilding.devices || {}, building.devices),
            hasMoreEvents,
            credentials,
            credentialsOriginal: credentials,
            broadcastMessage,
            userIds: users.map(u => u.id),
        };

        return {
            ...addEvents(state, building.id, events),
            data: {
                ...state.data,
                [building.id]: {
                    ...currentBuilding,
                    ..._building,
                },
            },
            loading: omit(state.loading, urlSlugOrId),
        };
    },
    [Actions.GET_BUILDING_FAILURE]: (state: State, action: any) => {
        const { urlSlugOrId } = action.meta;
        return {
            ...state,
            loading: omit(state.loading, urlSlugOrId),
            error: action.payload.message,
        };
    },

    [Actions.BUILDING_FETCHED]: (state: State, action: ReturnType<typeof buildingFetched>) => {
        const { building, devices } = action.payload;
        const currentBuilding = state.data[building.id] || {};

        // Sticking EcsDevice info into Device record... not perfect solution.
        // For our purpose on ECS video calls via an external ID, we just need
        // to capture the ID and display name.
        const _building = {
            ...building,
            devices: devices.reduce((all, { deviceId, ...ecsDevice }) => ({
                ...all,
                [deviceId]: {
                    id: deviceId,
                    ...ecsDevice,
                },
            }), {}),
        };

        return {
            ...state,
            data: {
                ...state.data,
                [building.id]: {
                    ...currentBuilding,
                    ..._building,
                },
            },
        };
    },

    /* KILL
    [Actions.UPDATE_BUILDING_REQUEST]: (state: State, action: any) => {
        return state;
    },
*/
    [Actions.UPDATE_BUILDING_SUCCESS]: (state: State, action: ReturnType<typeof updateBuildingSuccess>) => {
        const building = action.payload;
        const { data } = state;
        // TODO: separate out client-side data into a different data store
        return {
            ...state,
            data: {
                ...data,
                [building.id]: {
                    ...omit(data[building.id] || {}, 'properties'),
                    ...building,
                },
            },
        };
    },
    /* KILL:
    [Actions.GET_BUILDING_FAILURE]: (state: State, action: any) => {
        return state;
    },
*/

    [Actions.DELETE_BUILDING_REQUEST]: (state: State, action: any) => {
        //const id = action.payload;
        return state;
    },
    [Actions.DELETE_BUILDING_SUCCESS]: (state: State, action: any) => {
        const { id } = action.meta;
        // TODO: other reducers need to add devices, themes, and users
        return {
            ...state,
            data: omit(state.data, id),
            lastSearch: {
                ...state.lastSearch,
                order: filter(state.lastSearch.order, orderId => orderId !== id),
            },
        };
    },
    [Actions.DELETE_BUILDING_FAILURE]: (state: State, action: any) => {
        //const { id } = action.meta;
        return merge({}, state, { error: action.payload.message });
    },

    [Actions.GET_EVENT_LOG_REQUEST]: (state: State, action: any) => {
        const { buildingId } = action.payload;
        return merge({}, state, { data: { [buildingId]: {
            eventsLoading: true,
            eventsError: null,
        } } });
    },
    [Actions.GET_EVENT_LOG_SUCCESS]: (state: State, action: ReturnType<typeof getEventLogSuccess>) => {
        const { events, hasMore } = action.payload;
        const { buildingId } = action.meta;
        return merge(
            addEvents(state, buildingId, events),
            { data: { [buildingId]: {
                hasMoreEvents: hasMore,
                eventsLoading: false,
                eventsError: null,
            } } }
        );
    },
    [Actions.GET_EVENT_LOG_FAILURE]: (state: State, action: any) => {
        const { buildingId } = action.meta;
        const { message } = action.payload;
        return merge({}, state, { data: { [buildingId]: {
            eventsLoading: false,
            eventsError: message,
        } } });
    },

    [Actions.SET_CREDENTIALS_REQUEST]: (state: State, action: any) => {
        const { buildingId } = action.payload;
        return merge({}, state, { data: { [buildingId]: { savingCredentials: true } } });
    },
    [Actions.SET_CREDENTIALS_SUCCESS]: (state: State, action: any) => {
        const credentials = action.payload;
        const { buildingId } = action.meta;
        return merge({}, state, { data: { [buildingId]: {
            savingCredentials: false,
            credentials,
            credentialsOriginal: credentials,
        } } });
    },
    [Actions.SET_CREDENTIALS_FAILURE]: (state: State, action: any) => {
        //const error = action.payload;
        const { buildingId } = action.meta;
        return merge({}, state, { data: { [buildingId]: { savingCredentials: false } } });
    },

    [Actions.NEW_CREDENTIALS]: (state: State, action: any) => {
        const { buildingId } = action.meta;
        return updateBuildingCredentials(state, buildingId, credentials => (credentials || []).concat({
            provider: 'thomson-reuters', // TODO: could pull this from action.payload...
            appId: '',
            username: '',
            password: '',
        }));
    },
    [Actions.CHANGE_CREDENTIALS]: (state: State, action: any) => {
        const { buildingId, provider } = action.meta;
        return updateBuildingCredentials(state, buildingId, credentials => (credentials || []).map(c =>
            c.provider === provider
                ? action.payload
                : c
        ));
    },
    [Actions.DELETE_CREDENTIALS]: (state: State, action: any) => {
        const provider = action.payload;
        const { buildingId } = action.meta;
        return updateBuildingCredentials(state, buildingId, credentials => (credentials || []).filter(c => c.provider !== provider));
    },

    [Actions.SET_BROADCAST_REQUEST]: (state: State, action: any) => {
        const message = action.payload;
        const { buildingId } = action.meta;
        return merge({}, state, { data: { [buildingId]: {
            broadcastMessage: message,
            settingBroadcast: true,
            settingBroadcastError: null,
            previousBroadcastMessage: state.data[buildingId].broadcastMessage,
        } } });
    },
    [Actions.SET_BROADCAST_SUCCESS]: (state: State, action: any) => {
        const { buildingId } = action.meta;
        return merge({}, state, { data: { [buildingId]: {
            settingBroadcast: false,
            settingBroadcastError: null,
        } } });
    },
    [Actions.SET_BROADCAST_FAILURE]: (state: State, action: any) => {
        const err = action.payload;
        const { buildingId } = action.meta;
        return merge({}, state, { data: { [buildingId]: {
            broadcastMessage: state.data[buildingId].previousBroadcastMessage,
            settingBroadcast: false,
            settingBroadcastError: err.message,
        } } });
    },
    

    //
    //  Device Actions
    //

    [DeviceActions.CREATE_DEVICES_SUCCESS]: (state: State, action: ReturnType<typeof createDevicesSuccess>) => {
        const devices = action.payload;
        const { buildingId } = action.meta;
        return addDevices(state, buildingId, devices);
    },

    [DeviceActions.UPDATE_DEVICE_SUCCESS]: (state: State, action: ReturnType<typeof updateDeviceSuccess>) => {
        const device = action.payload;
        return addDevices(state, device.buildingId, [ device ]);
    },


    [DeviceActions.GROUP_DEVICES_SUCCESS]: (state: State, action: any) => {
        const devices: Device[] = action.payload;
        const { buildingId } = action.meta;
        return addDevices(state, buildingId, devices);
    },
    [DeviceActions.GROUP_DEVICES_FAILURE]: (state: State, action: any) => {
        return merge({}, state, { error: action.payload.message });
    },


    [DeviceActions.DELETE_DEVICE_SUCCESS]: (state: State, action: any) => {
        const { buildingId, deviceId } = action.meta;
        const building = state.data[buildingId];
        if (!building) return state;
        return {
            ...state,
            data: {
                ...state.data,
                [buildingId]: {
                    ...building,
                    devices: omit(building.devices, deviceId),
                },
            },
        };
    },


    //[DeviceActions.DISABLE_DEVICE_REQUEST]: (state: State, action: any) => {
    //},
    [DeviceActions.DISABLE_DEVICE_SUCCESS]: (state: State, action: any) => {
        const { buildingId, deviceId } = action.meta;
        return merge({}, state, { data: { [buildingId]: { deviceStatus: { [deviceId]: DeviceStatus.Offline } } } });
    },
    //[DeviceActions.DISABLE_DEVICE_FAILURE]: (state: State, action: any) => {
    //},


    //[DeviceActions.GET_DEVICE_DETAILS_REQUEST]: (state: State, action: any) => {
    //},
    [DeviceActions.GET_DEVICE_DETAILS_SUCCESS]: (state: State, action: any) => {
        const { script, files } = action.payload;
        const { buildingId, deviceId } = action.meta;
        const formatted = JSON.stringify(JSON.parse(script || defaultDeviceScript), null, '  ');
        return merge({}, state, { data: { [buildingId]: { devices: { [deviceId]: {
            script: formatted,
            originalScript: formatted,
            files: files.reduce((obj: any, f: DeviceFile) => ({ ...obj, [f.fileId]: f }), {} as any),
        } } } } });
    },
    //[DeviceActions.GET_DEVICE_DETAILS_FAILURE]: (state: State, action: any) => {
    //},

    //[DeviceActions.DELETE_DEVICE_FILE_REQUEST]: (state: State, action: any) => {
    //},
    [DeviceActions.DELETE_DEVICE_FILE_SUCCESS]: (state: State, action: any) => {
        const { buildingId, deviceId, fileId } = action.meta;
        const building = state.data[buildingId];
        if (!building) return state;
        if (!building.devices) return state;
        const device = building.devices[deviceId];
        if (!device) return state;
        const files = omit(device.files, fileId);
        return {
            ...state,
            data: {
                ...state.data,
                [buildingId]: {
                    ...building,
                    devices: {
                        ...building.devices,
                        [deviceId]: {
                            ...device,
                            files,
                        },
                    },
                },
            },
        };
    },
    //[DeviceActions.DELETE_DEVICE_FILE_FAILURE]: (state: State, action: any) => {
    //},

    [DeviceActions.UPDATE_DEVICE_SCRIPT]: (state: State, action: any) => {
        const { buildingId, deviceId, script } = action.payload;
        return merge({}, state, { data: { [buildingId]: { devices: { [deviceId]: { script } } } } });
    },

    //[DeviceActions.PUBLISH_DEVICE_SCRIPT_REQUEST]: (state: State, action: any) => {
    //},
    [DeviceActions.PUBLISH_DEVICE_SCRIPT_SUCCESS]: (state: State, action: any) => {
        const { buildingId, deviceId, script } = action.meta;
        return merge({}, state, { data: { [buildingId]: { devices: { [deviceId]: {
            script,
            originalScript: script,
        } } } } });
    },
    //[DeviceActions.PUBLISH_DEVICE_SCRIPT_FAILURE]: (state: State, action: any) => {
    //},

    //[DeviceActions.CANCEL_DEVICE_SCRIPT_REQUEST]: (state: State, action: any) => {
    //},
    //[DeviceActions.CANCEL_DEVICE_SCRIPT_SUCCESS]: (state: State, action: any) => {
    //},
    //[DeviceActions.CANCEL_DEVICE_SCRIPT_FAILURE]: (state: State, action: any) => {
    //},


    //
    //  User Actions
    //

    [UserActions.CREATE_USER_SUCCESS]: (state: State, action: ReturnType<typeof createUserSuccess>) => {
        const { buildingId } = action.meta;
        const { id: userId }: User = action.payload;
        // When a user is created and assigned to a building at the same time,
        // we need to make sure to register the new user's ID in the building's list of user IDs
        return updateBuildingUsers(state, buildingId, userIds => (userIds || []).concat(userId));
    },


    //
    //  Building User Actions
    //

    [BuildingUserActions.ASSIGN_BUILDING_TO_USER_SUCCESS]: (state: State, action: ReturnType<typeof assignBuildingUserSuccess>) => {
        const { payload: { userId, buildingId } } = action;
        return updateBuildingUsers(state, buildingId, userIds => (userIds || []).concat(userId));
    },
    [BuildingUserActions.UNASSIGN_BUILDING_FROM_USER_SUCCESS]: (state: State, action: any) => {
        const { payload: { userId, buildingId } } = action;
        return updateBuildingUsers(state, buildingId, userIds => (userIds || []).filter(id => id !== userId));
    },


    //
    //  Editor Actions
    //
    [EditorActions.SAVE_THEME_SUCCESS]: (state: State, action: any) => {
        const { theme, buildingId } = action.payload;
        return updateBuildingThemes(state, buildingId, themeIds => (themeIds || []).concat(theme.id));
    },


    //
    //  Theme Actions
    //
    [ThemeActions.COPY_THEME_TO_BUILDING_SUCCESS]: (state: State, action: any) => {
        const { id } = action.payload;
        const { buildingId } = action.meta; // meta also has a themeId but it's the source themeId
        return updateBuildingThemes(state, buildingId, themeIds =>
            !!themeIds && themeIds.indexOf(id) >= 0
                ? themeIds
                : (themeIds || []).concat(id)
        );
    },
    [ThemeActions.UPLOAD_THEME_SUCCESS]: (state: State, action: any) => {
        const { id } = action.payload;
        const { buildingId } = action.meta;
        return updateBuildingThemes(state, buildingId, themeIds =>
            !!themeIds && themeIds.indexOf(id) >= 0
                ? themeIds
                : (themeIds || []).concat(id)
        );
    },
    // TODO: Delete themeId from buildings when a theme is deleted?  (didn't seem to harm the system to NOT do this (tested with shared theme))


    //
    //  Session Actions
    //
    [SessionActions.LOGOUT_SUCCESS]: () => {
        return initialState;
    },
} as any, initialState);
